/**
 * Grades Service - API client for subjects, grade entry,
 * and report card generation
 */
import api from '../utils/api';

const gradesService = {
  // ---- Grading scale ----

  getGesScale: async () => {
    try {
      return await api.get('/api/grades/ges-scale');
    } catch (error) {
      console.error('Error fetching GES grading scale:', error);
      throw error;
    }
  },

  // ---- Grading schemes (multiple configurable grading systems, one per
  // class level and/or subject — see services/grading_service.py) ----

  listGradingSchemes: async () => {
    try {
      return await api.get('/api/grades/grading-schemes');
    } catch (error) {
      console.error('Error fetching grading schemes:', error);
      throw error;
    }
  },

  createGradingScheme: async (data) => {
    try {
      return await api.post('/api/grades/grading-schemes', data);
    } catch (error) {
      console.error('Error creating grading scheme:', error);
      throw error;
    }
  },

  updateGradingScheme: async (schemeId, data) => {
    try {
      return await api.put(`/api/grades/grading-schemes/${schemeId}`, data);
    } catch (error) {
      console.error('Error updating grading scheme:', error);
      throw error;
    }
  },

  deleteGradingScheme: async (schemeId) => {
    try {
      return await api.delete(`/api/grades/grading-schemes/${schemeId}`);
    } catch (error) {
      console.error('Error deleting grading scheme:', error);
      throw error;
    }
  },

  // ---- Subjects ----

  listSubjects: async () => {
    try {
      return await api.get('/api/grades/subjects');
    } catch (error) {
      console.error('Error fetching subjects:', error);
      throw error;
    }
  },

  seedDefaultSubjects: async () => {
    try {
      return await api.post('/api/grades/subjects/seed-defaults', {});
    } catch (error) {
      console.error('Error seeding default subjects:', error);
      throw error;
    }
  },

  createSubject: async (data) => {
    try {
      return await api.post('/api/grades/subjects', data);
    } catch (error) {
      console.error('Error creating subject:', error);
      throw error;
    }
  },

  // ---- Grades ----

  getClassGrades: async (classId, params = {}) => {
    try {
      return await api.get(`/api/grades/class/${classId}`, { params });
    } catch (error) {
      console.error('Error fetching class grades:', error);
      throw error;
    }
  },

  bulkSubmitGrades: async (grades) => {
    try {
      return await api.post('/api/grades/bulk', grades);
    } catch (error) {
      console.error('Error submitting bulk grades:', error);
      throw error;
    }
  },

  createGrade: async (data) => {
    try {
      return await api.post('/api/grades', data);
    } catch (error) {
      console.error('Error recording grade:', error);
      throw error;
    }
  },

  updateGrade: async (gradeId, data) => {
    try {
      return await api.patch(`/api/grades/${gradeId}`, data);
    } catch (error) {
      console.error('Error updating grade:', error);
      throw error;
    }
  },

  deleteGrade: async (gradeId) => {
    try {
      return await api.delete(`/api/grades/${gradeId}`);
    } catch (error) {
      console.error('Error deleting grade:', error);
      throw error;
    }
  },

  // ---- Report cards ----

  previewReportCard: async (studentId, academicTermId) => {
    try {
      return await api.get(`/api/grades/report-cards/${studentId}/${academicTermId}/preview`);
    } catch (error) {
      if (error.message !== 'Report card not yet available') {
        console.error('Error previewing report card:', error);
      }
      throw error;
    }
  },

  downloadReportCard: async (studentId, academicTermId) => {
    try {
      return await api.get(`/api/grades/report-cards/${studentId}/${academicTermId}/download`, { raw: true });
    } catch (error) {
      console.error('Error downloading report card:', error);
      throw error;
    }
  },

  bulkPreviewReportCards: async (classId, termId) => {
    try {
      return await api.get('/api/grades/report-cards/bulk-preview', { params: { class_id: classId, term_id: termId } });
    } catch (error) {
      console.error('Error previewing bulk report cards:', error);
      throw error;
    }
  },

  bulkDownloadReportCards: async (payload) => {
    try {
      return await api.post('/api/grades/report-cards/bulk-download', payload, { raw: true });
    } catch (error) {
      console.error('Error downloading bulk report cards:', error);
      throw error;
    }
  },

  approveReportCard: async (studentId, academicTermId) => {
    try {
      return await api.post(`/api/grades/report-cards/${studentId}/${academicTermId}/approve`, {});
    } catch (error) {
      console.error('Error approving report card:', error);
      throw error;
    }
  },

  // Calls back an already-approved report card for correction — hides it
  // from the parent/student again and notifies the parent why. Re-approve
  // (above) once corrected to resend.
  recallReportCard: async (studentId, academicTermId, reason) => {
    try {
      return await api.post(`/api/grades/report-cards/${studentId}/${academicTermId}/recall`, { reason });
    } catch (error) {
      console.error('Error recalling report card:', error);
      throw error;
    }
  },

  getReportCardRecallHistory: async (studentId, academicTermId) => {
    try {
      return await api.get(`/api/grades/report-cards/${studentId}/${academicTermId}/recall-history`);
    } catch (error) {
      console.error('Error fetching report card recall history:', error);
      throw error;
    }
  },

  // School-wide list of recalled report cards. resolved: true = already
  // corrected and resent, false = still outstanding, omit for both.
  listReportCardRecalls: async (resolved) => {
    try {
      const params = resolved === undefined ? {} : { resolved };
      return await api.get('/api/grades/report-cards/recalls', { params });
    } catch (error) {
      console.error('Error fetching report card recalls:', error);
      throw error;
    }
  },

  bulkApproveReportCards: async (classId, termId) => {
    try {
      return await api.post('/api/grades/report-cards/bulk-approve', {}, { params: { class_id: classId, term_id: termId } });
    } catch (error) {
      console.error('Error bulk-approving report cards:', error);
      throw error;
    }
  },

  // Generates (or re-generates) the report card, recomputing scores/position
  // and saving the narrative fields — class/head teacher remarks, attitude,
  // conduct, interest, vacation/reopening dates, promoted_to.
  generateReportCard: async (data) => {
    try {
      return await api.post('/api/grades/report-cards/generate', data);
    } catch (error) {
      console.error('Error generating report card:', error);
      throw error;
    }
  },

  suggestRemarks: async (studentId, academicTermId) => {
    try {
      return await api.get(`/api/grades/report-cards/${studentId}/${academicTermId}/suggest-remarks`);
    } catch (error) {
      console.error('Error fetching suggested remarks:', error);
      throw error;
    }
  },

  aiSuggestRemarks: async (studentId, academicTermId) => {
    try {
      return await api.post(`/api/grades/report-cards/${studentId}/${academicTermId}/ai-suggest-remarks`, {});
    } catch (error) {
      console.error('Error fetching AI-suggested remarks:', error);
      throw error;
    }
  },

  // ---- Standards-based (mastery) grading — parallel/optional alongside
  // the numeric grade entry above ----

  createMasteryRecord: async (data) => {
    try {
      return await api.post('/api/grades/mastery-records', data);
    } catch (error) {
      console.error('Error recording mastery assessment:', error);
      throw error;
    }
  },

  getStudentMasteryRecords: async (studentId, params = {}) => {
    try {
      return await api.get(`/api/students/${studentId}/mastery-records`, { params });
    } catch (error) {
      console.error('Error fetching student mastery records:', error);
      throw error;
    }
  }
};

export default gradesService;
